import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { LogoFull } from '@/components/layout/logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { BackupCodesGrid } from '@/features/mfa/components/backup-codes-grid';
import { useEnrollTotp, useVerifyEnrollment } from '@/features/mfa/hooks/use-mfa';
import { parseMfaError } from '@/features/mfa/lib/parse-error';
import { getMfaStatus } from '@/features/mfa/services/mfa.service';
import { supabase } from '@/lib/supabase';

import type { EnrollTotpResponse } from '@/features/mfa/types';

// /auth/mfa/enroll — flujo de alta de TOTP.
//
// Stages:
//   1. 'qr'     → backend devuelve qr_code+secret+factor_id; user escanea
//                  con app authenticator e ingresa el código de 6 dígitos.
//   2. 'codes'  → enrollment verified; mostramos los 10 backup codes con
//                  ack checkbox required antes de navegar.
//
// Auth gate: requiere session válida (AAL1+). Si no hay session, redirige
// a /login. NO requiere AAL2 — este es el flujo para PROMOVER de AAL1 a
// AAL2 por primera vez.

export const Route = createFileRoute('/auth/mfa/enroll')({
  component: EnrollPage,
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: '/login' });
    }
    // Guard: si el user YA está enrolled, evitar disparar enroll-totp de nuevo
    // (Supabase devolvería 400 ENROLL_FAILED al haber un factor verificado
    // existente). Mandar a /settings/security donde se ve el estado actual y
    // las opciones de reset/regenerate.
    try {
      const status = await getMfaStatus();
      if (status.enrolled) {
        throw redirect({ to: '/settings/security' });
      }
    } catch (e) {
      // Si el throw fue del redirect, re-lanzar. Si fue otro error, dejar
      // pasar al enroll (peor caso: el 400 que ya teníamos antes del guard).
      if (e && typeof e === 'object' && 'href' in e) throw e;
    }
  },
});

function EnrollPage() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-4 py-12"
      style={{ background: 'var(--color-bg)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <LogoFull />
        </div>
        <EnrollFlow />
        <p className="mt-8 text-center text-xs text-[color:var(--color-fg-subtle)]">
          Simplificalo · Configuración de seguridad
        </p>
      </div>
    </div>
  );
}

function EnrollFlow() {
  const [stage, setStage] = useState<'qr' | 'codes'>('qr');
  const [enrollment, setEnrollment] = useState<EnrollTotpResponse | null>(null);
  const [backupCodes, setBackupCodes] = useState<readonly string[] | null>(null);

  const enrollMutation = useEnrollTotp();

  // Ref para prevenir doble ejecución en React StrictMode (dev). Sin esto
  // el useEffect dispara enroll-totp dos veces — la segunda falla con
  // "factor already exists" porque Supabase no permite dos factores con el
  // mismo friendlyName. El backend ahora limpia factores huérfanos, pero
  // evitar la llamada duplicada sigue siendo correcto.
  const enrollCalledRef = useRef(false);
  useEffect(() => {
    if (enrollment || enrollCalledRef.current) return;
    enrollCalledRef.current = true;
    enrollMutation
      .mutateAsync()
      .then((res) => setEnrollment(res))
      .catch(() => {
        // El estado de error lo lee el render desde enrollMutation.error.
        // Reset ref para permitir reintento si el user refresca.
        enrollCalledRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stage === 'codes' && backupCodes) {
    return <BackupCodesStep codes={backupCodes} />;
  }

  return (
    <div
      className="rounded-xl border p-6 shadow-2xl"
      style={{
        background: 'var(--color-bg-elevated)',
        borderColor: 'var(--color-border)',
      }}
    >
      <h1 className="mb-1 text-lg font-semibold tracking-tight">Activar autenticación de 2 factores</h1>
      <p className="mb-6 text-sm text-[color:var(--color-fg-muted)]">
        Escaneá el QR con tu app authenticator (Google Authenticator, 1Password, Authy, etc.) y luego
        ingresá el código de 6 dígitos que te muestre.
      </p>

      {enrollMutation.isError ? (
        <ErrorPanel
          message="No pudimos iniciar el enrollment. Refresca la página para reintentar."
        />
      ) : !enrollment ? (
        <QrSkeleton />
      ) : (
        <QrStep
          enrollment={enrollment}
          onVerified={(codes) => {
            setBackupCodes(codes);
            setStage('codes');
          }}
        />
      )}
    </div>
  );
}

function QrSkeleton() {
  return (
    <div className="flex flex-col items-center gap-4">
      <Skeleton className="h-48 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function QrStep({
  enrollment,
  onVerified,
}: {
  readonly enrollment: EnrollTotpResponse;
  readonly onVerified: (codes: readonly string[]) => void;
}) {
  const [code, setCode] = useState('');
  const [showManual, setShowManual] = useState(false);
  const verify = useVerifyEnrollment();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (code.length !== 6) return;
    try {
      const res = await verify.mutateAsync({ factor_id: enrollment.factor_id, code });
      onVerified(res.backup_codes);
    } catch (err) {
      const parsed = parseMfaError(err);
      toast.error(parsed?.message ?? 'Código inválido. Probá de nuevo.');
      setCode('');
    }
  };

  const copySecret = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      toast.success('Secreto copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex flex-col items-center">
        <div
          className="rounded-lg border p-3"
          style={{
            background: '#fff',
            borderColor: 'var(--color-border)',
          }}
        >
          <img src={enrollment.qr_code} alt="QR code TOTP" className="h-44 w-44" />
        </div>
        <button
          type="button"
          className="mt-3 text-xs underline-offset-2 hover:underline"
          style={{ color: 'var(--color-fg-muted)' }}
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? 'Ocultar' : '¿No puedes escanear?'} ingresar el secreto manualmente
        </button>
      </div>

      {showManual ? (
        <div
          className="rounded-md border p-3 text-xs"
          style={{
            background: 'var(--color-bg-overlay)',
            borderColor: 'var(--color-border)',
          }}
        >
          <p className="mb-1 text-[color:var(--color-fg-muted)]">Secreto (base32):</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all font-mono text-[11px]">{enrollment.secret}</code>
            <Button type="button" variant="ghost" size="sm" onClick={() => void copySecret()}>
              Copiar
            </Button>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="totp-code">Código de 6 dígitos</Label>
        <Input
          id="totp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center font-mono text-lg tracking-[0.5em]"
        />
      </div>

      <Button type="submit" disabled={code.length !== 6 || verify.isPending} className="w-full">
        {verify.isPending ? <Spinner size="sm" /> : 'Verificar y activar'}
      </Button>
    </form>
  );
}

function BackupCodesStep({ codes }: { readonly codes: readonly string[] }) {
  const navigate = useNavigate();
  const [acked, setAcked] = useState(false);

  return (
    <div
      className="rounded-xl border p-6 shadow-2xl"
      style={{
        background: 'var(--color-bg-elevated)',
        borderColor: 'var(--color-border)',
      }}
    >
      <h1 className="mb-1 text-lg font-semibold tracking-tight">Guardá tus códigos de respaldo</h1>
      <p className="mb-5 text-sm text-[color:var(--color-fg-muted)]">
        Estos códigos te permiten iniciar sesión si perdés acceso a tu app authenticator. Cada uno
        sirve <strong>una sola vez</strong>. <strong>No los volverás a ver</strong> — guardalos antes de continuar.
      </p>

      <BackupCodesGrid
        codes={codes}
        requireAcknowledgment
        onAcknowledged={() => setAcked(true)}
      />

      <Button
        type="button"
        className="mt-6 w-full"
        disabled={!acked}
        onClick={() => void navigate({ to: '/' })}
      >
        Continuar al dashboard
      </Button>
    </div>
  );
}

function ErrorPanel({ message }: { readonly message: string }) {
  return (
    <div
      className="rounded-md border p-3 text-sm"
      style={{
        background: 'rgba(239, 68, 68, 0.06)',
        borderColor: 'rgba(239, 68, 68, 0.25)',
        color: 'rgb(185, 28, 28)',
      }}
    >
      {message}
    </div>
  );
}
